import {
  Component,
  lazy,
  Suspense,
  useMemo,
  useState,
  type ComponentType,
  type ErrorInfo,
  type LazyExoticComponent,
  type ReactNode
} from "react";

type LazyModule<TProps> = {
  default: ComponentType<TProps>;
};

interface LazyChunkErrorStateProps {
  surfaceName: string;
  onRetry: () => void;
}

interface LazyChunkErrorBoundaryProps extends LazyChunkErrorStateProps {
  onRepeatedFailureReload?: (() => void) | undefined;
  resetKey: number;
  children: ReactNode;
}

interface LazyChunkErrorBoundaryState {
  error: unknown;
}

export function LazyChunkErrorState({
  surfaceName,
  onRetry
}: LazyChunkErrorStateProps) {
  return (
    <section
      role="alert"
      aria-live="assertive"
      className="max-w-[760px] rounded-[14px] border border-[#B4452F]/35 bg-[#B4452F]/[0.08] px-5 py-4 text-[#8F3726]"
    >
      <div className="text-[10.5px] font-bold uppercase tracking-[.1em]">
        {surfaceName}
      </div>
      <h2 className="m-0 mt-1 text-[22px] font-extrabold tracking-[-.02em] text-[var(--ink)]">
        {surfaceName} could not load
      </h2>
      <p className="m-0 mt-2 max-w-[560px] text-[13px] font-semibold leading-[1.5] text-[#7B4438]">
        This section didn&apos;t load &mdash; check your connection and retry.
      </p>
      <button
        type="button"
        onClick={onRetry}
        className="mt-4 rounded-[10px] border border-[#B4452F]/40 bg-[var(--surface)] px-3.5 py-2 text-[12px] font-extrabold text-[#8F3726] transition-colors hover:bg-[#F8E8E5] motion-reduce:transition-none"
      >
        Retry
      </button>
    </section>
  );
}

class LazyChunkErrorBoundary extends Component<
  LazyChunkErrorBoundaryProps,
  LazyChunkErrorBoundaryState
> {
  private reloadScheduledForKey: number | null = null;

  state: LazyChunkErrorBoundaryState = {
    error: null
  };

  static getDerivedStateFromError(error: unknown): LazyChunkErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: unknown, errorInfo: ErrorInfo) {
    console.warn("Lazy route chunk failed", error, errorInfo.componentStack);

    if (
      this.props.resetKey > 0 &&
      this.props.onRepeatedFailureReload &&
      this.reloadScheduledForKey !== this.props.resetKey
    ) {
      this.reloadScheduledForKey = this.props.resetKey;
      globalThis.setTimeout(() => {
        this.props.onRepeatedFailureReload?.();
      }, 0);
    }
  }

  componentDidUpdate(previousProps: LazyChunkErrorBoundaryProps) {
    if (
      previousProps.resetKey !== this.props.resetKey &&
      this.state.error !== null
    ) {
      this.setState({ error: null });
    }

    if (previousProps.resetKey !== this.props.resetKey) {
      this.reloadScheduledForKey = null;
    }
  }

  render() {
    if (this.state.error !== null) {
      return (
        <LazyChunkErrorState
          surfaceName={this.props.surfaceName}
          onRetry={this.props.onRetry}
        />
      );
    }

    return this.props.children;
  }
}

export function RetryableLazyRoute<TProps>({
  load,
  fallback,
  surfaceName,
  onRepeatedFailureReload,
  render
}: {
  load: (attempt: number) => Promise<LazyModule<TProps>>;
  fallback: ReactNode;
  surfaceName: string;
  onRepeatedFailureReload?: (() => void) | undefined;
  render: (LazyComponent: LazyExoticComponent<ComponentType<TProps>>) => ReactNode;
}) {
  const [attempt, setAttempt] = useState(0);
  const LazyComponent = useMemo(() => lazy(() => load(attempt)), [attempt, load]);
  const retry = () => setAttempt((current) => current + 1);

  return (
    <LazyChunkErrorBoundary
      resetKey={attempt}
      surfaceName={surfaceName}
      onRetry={retry}
      onRepeatedFailureReload={onRepeatedFailureReload}
    >
      <Suspense fallback={fallback}>{render(LazyComponent)}</Suspense>
    </LazyChunkErrorBoundary>
  );
}
